import { useMemo, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { useAppSelector } from "../../common/hooks"
import { selectUserRoles } from "../../redux/slices/auth"
import { checkIsAdmin } from "../../common/utils"
import BrandActivities from "../../common/ui/brand-details/activities"
import BrandDetails from "../../common/ui/brand-details/details"
import BrandDetailsHeader from "../../common/ui/brand-details/header"
import BrandDetailsIntegrations from "../../common/ui/integrations/integrationTable"
import BrandDetailsLeads from "../../common/ui/brand-details/leads"
import BrandDetailsLoanOfficers from "../../common/ui/brand-details/loan-officers/BrandDetailsLoanOfficers"
import EngagementsTable from "../../common/ui/engagements/table"
import PricingLogs from "../../common/ui/pricing-logs"
import PricingLogCardDetails from "../../common/ui/pricing-logs/pricing-log-card-details"
import ManageBrand from "../../common/ui/brands/manage"
import BBDialog from "../../common/ui/dialog"
import Tabs from "../../common/ui/tabs"
import { RequireRole } from "../../common/validation"
import {
  useFetchIntegrationsQuery,
  useFetchLeadsByBrandQuery,
  useFetchBrandQuery,
  useFetchOrgDetailsQuery
} from "../../redux/queries"
import { Integration } from "../../types"
import { If } from "../../common/ui"
import { BrandDetailsSkeleton } from "../../common/ui/skeletons"

const BrandDetailsPage = () => {
  const { brandId: id, orgId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const userRoles = useAppSelector(selectUserRoles)
  const isAdmin = checkIsAdmin(userRoles)

  const initialTab =
    new URLSearchParams(location.search).get("tab") ?? "brand-details"

  const { data: orgDetails } = useFetchOrgDetailsQuery(
    { id: orgId ?? "" },
    { skip: !orgId }
  )
  const { data: brand, isFetching } = useFetchBrandQuery(id ?? "", {
    skip: !id
  })

  const { data: leads, isLoading: isLoadingLeads } = useFetchLeadsByBrandQuery(
    id ?? "",
    { skip: !id }
  )

  const [selectedPricingLogId, setSelectedPricingLogId] = useState<
    string | null
  >(null)

  const { data: integrationsResponse, isLoading: isLoadingIntegrationsAll } =
    useFetchIntegrationsQuery(
      {
        org_id: brand?.org_id,
        brand_id: id as string,
        full_detail: true,
        limit: 999
      },
      {
        skip: !brand?.org_id,
        refetchOnMountOrArgChange: true
      }
    )

  const sortedIntegrations = useMemo(() => {
    const results = (integrationsResponse?.results as Integration[]) ?? []
    return results
      .filter(({ brand_id }) => brand_id === id)
      .sort(
        (a, b) =>
          parseInt(b.created ?? "0", 10) - parseInt(a.created ?? "0", 10)
      )
  }, [id, integrationsResponse?.results])

  const todayStart = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return Math.floor(d.getTime() / 1000)
  }, [])

  const todayIntegrations = useMemo(
    () =>
      sortedIntegrations.filter(
        i => parseInt(i.created ?? "0", 10) >= todayStart
      ),
    [sortedIntegrations, todayStart]
  )

  const historyIntegrations = useMemo(
    () =>
      sortedIntegrations.filter(
        i => parseInt(i.created ?? "0", 10) < todayStart
      ),
    [sortedIntegrations, todayStart]
  )

  const handleSelectIntegration = (integrationId: string) => {
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search
    )
    const breadcrumbs = [
      { label: "Brands", href: "/brands" },
      { label: brand?.name ?? "Brand", href: location.pathname },
      { label: "Integrations", href: `${location.pathname}?tab=integrations` }
    ]

    navigate(`/integrations/${integrationId}?returnUrl=${returnUrl}`, {
      state: { from: breadcrumbs }
    })
  }

  const [manageBrandVisible, setManageBrandVisible] = useState(false)
  const [deleteBrandVisible, setDeleteBrandVisible] = useState(false)

  const [tab, setTab] = useState(initialTab)

  const tabContent = {
    "brand-details": brand && <BrandDetails brand={brand} />,
    activity: brand && <BrandActivities brand={brand} />,
    "loan-officers": <BrandDetailsLoanOfficers brandId={id} />,
    leads: (
      <BrandDetailsLeads
        data={leads ?? []}
        isLoading={isLoadingLeads}
        brandId={id}
      />
    ),
    subscriptions: (
      <div className="p-6">
        <EngagementsTable
          brandId={id}
          brandName={brand?.name}
          showStats={false}
          showDateFilter={false}
        />
      </div>
    ),
    integrations: (
      <BrandDetailsIntegrations
        todayIntegrations={todayIntegrations}
        integrations={historyIntegrations}
        isLoading={isLoadingIntegrationsAll}
        onSelect={handleSelectIntegration}
        showTypeTabs
        isAdmin={isAdmin}
        approvedUrlsOrgId={brand?.org_id}
      />
    ),
    "pricing-log": (
      <PricingLogs id={{ bank_id: id }} onSelect={setSelectedPricingLogId} />
    )
  }

  if (!brand?.id || !id)
    return (
      <If
        condition={!isFetching}
        then={<div className="p-6">Brand not found</div>}
        else={<BrandDetailsSkeleton />}
      />
    )

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.brands}>
      <div className="h-full bg-white">
        <BrandDetailsHeader
          brand={brand}
          onClickDelete={() => setDeleteBrandVisible(true)}
          onClickEdit={() => setManageBrandVisible(true)}
          breadcrumbs={
            orgDetails
              ? [
                  { name: "Organizations", href: "/organizations" },
                  {
                    name: orgDetails.name,
                    href: `/organizations/${orgDetails.id}`
                  },
                  { name: brand.name, href: "#", current: true }
                ]
              : undefined
          }
        />
        <div>
          <Tabs
            className="px-6"
            currentTab={tab}
            tabs={[
              { id: "brand-details", label: "Brand Details" },
              { id: "activity", label: "Activity" },
              { id: "loan-officers", label: "Loan Officers" },
              { id: "leads", label: "Leads" },
              { id: "subscriptions", label: "Subscriptions" },
              { id: "integrations", label: "Brand Integrations" },
              { id: "pricing-log", label: "Pricing Log" }
            ]}
            onChange={setTab}
            urlParam="tab"
          />
          <div className="bg-white">
            {tabContent[tab as keyof typeof tabContent]}
          </div>
        </div>
        <ManageBrand
          open={manageBrandVisible}
          data={brand}
          isInSidePanel
          onClose={() => setManageBrandVisible(false)}
        />
        <BBDialog
          open={deleteBrandVisible}
          onClose={() => setDeleteBrandVisible(false)}
          title="Delete brand"
          body="By doing this all the information stored in the system about this brand will be deleted. Are you sure?"
          okLabel="Yes, Delete"
          cancelLabel="Cancel"
          onOk={() => {}}
          onCancel={() => setDeleteBrandVisible(false)}
        />
        <PricingLogCardDetails
          open={!!selectedPricingLogId}
          onClose={() => setSelectedPricingLogId(null)}
          data={
            selectedPricingLogId
              ? ({ id: selectedPricingLogId } as any)
              : undefined
          }
        />
      </div>
    </RequireRole>
  )
}

export default BrandDetailsPage
